
import { Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { catchError, finalize, of, retry, Subject, switchMap, exhaustMap, take, takeWhile, throwError, timer } from 'rxjs';
// Service Layer
import { NotificationsService } from 'src/app/services/aws/notification/notificationservice';
import { HouseEventService } from 'src/app/services/aws/house-and-well-being-events/house-event.service';

@Component({
    selector: 'app-upload-adhoc-notifications-dialog',
    templateUrl: './upload-adhoc-notifications-dialog.component.html',
    styleUrls: ['./upload-adhoc-notifications-dialog.component.scss'],
    standalone: false
})
export class UploadAdhocNotificationsDialogComponent implements OnDestroy {
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
    private readonly uploadedSubject = new Subject<{ uploaded: boolean; partial: boolean }>();
    readonly uploaded$ = this.uploadedSubject.asObservable();
    showGlobalSpinner = false;
    fileName = '';
    fileBase64: any = null; // base64
    fileError = false;
    fileNameMismatch = false;
    invalidFileMessage = '';
    disableUploadCta = true;
    invalidEvents: any[] = [];
    horizontalPosition: MatSnackBarHorizontalPosition = 'right';
    verticalPosition: MatSnackBarVerticalPosition = 'top';
    allowedFile = ['csv'];
    readonly templateCsvHref = 'assets/templates/AdhocNotification.csv';

    constructor(
        private dialogRef: MatDialogRef<UploadAdhocNotificationsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private snackBar: MatSnackBar,
        private notificationsService: NotificationsService,
        private houseEventService: HouseEventService
    ) { }

    ngOnDestroy(): void {
        this.uploadedSubject.complete();
    }
    close(): void {
        this.dialogRef.close();
    }
    openFilePicker(): void {
        if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.click();
        }
    }
    uploadFileEvt(event: any): void {
        this.invalidEvents = [];
        this.fileError = false;
        this.fileNameMismatch = false;
        this.invalidFileMessage = '';
        this.disableUploadCta = true;
        const selected: File | undefined = event?.target?.files?.[0];
        if (!selected) {
            this.resetFileSelection();
            return;
        }
        this.fileName = selected.name;
        const extension = this.fileName.split('.').pop()?.toLowerCase() || '';
        const isAllowed = this.allowedFile.includes(extension);
        const fileSizeKb = +(selected.size / 1024).toFixed(2); // KB
        const isTooLarge = fileSizeKb > 700;
        if (!isAllowed) {
            this.fileError = true;
            this.invalidFileMessage = 'ERROR: Please upload a valid .csv file.';
            this.disableUploadCta = true;
            this.fileBase64 = null;
            return;
        }
        if (isTooLarge) {
            this.fileError = true;
            this.invalidFileMessage = `ERROR: File size is ${fileSizeKb} KB. Please upload a file smaller than 700 KB.`;
            this.disableUploadCta = true;
            this.fileBase64 = null;
            this.snackBar.open(this.invalidFileMessage, 'OK', {
                duration: 8000,
                horizontalPosition: this.horizontalPosition,
                verticalPosition: this.verticalPosition,
                panelClass: ['adhoc-upload-snackbar', 'error-snackbar'],
            });
            return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(selected);
        reader.onload = () => {
            const dataUrl = typeof reader.result === 'string' ? reader.result : '';
            if (this.isCsvEmpty(dataUrl)) {
                this.fileError = true;
                this.invalidFileMessage = 'ERROR: The selected file is empty. Please select a valid CSV file with at least one notification.';
                this.disableUploadCta = true;
                this.fileBase64 = null;
                this.snackBar.open(this.invalidFileMessage, 'OK', {
                    duration: 8000,
                    horizontalPosition: this.horizontalPosition,
                    verticalPosition: this.verticalPosition,
                    panelClass: ['adhoc-upload-snackbar', 'error-snackbar'],
                });
                return;
            }
            this.fileBase64 = dataUrl; // base64 data url
            this.disableUploadCta = false;
        };
        reader.onerror = () => {
            this.fileError = true;
            this.invalidFileMessage = 'ERROR: Unable to read the selected file.';
            this.disableUploadCta = true;
            this.fileBase64 = null;
        };
    }
    uploadFile(): void {
        if (this.disableUploadCta) return;
        this.invalidEvents = [];
        this.showGlobalSpinner = true;
        this.notificationsService
            .fileUploadParticipants(this.fileBase64)
            .pipe(
                take(1),
                switchMap((res: any) => {
                    // If backend already says Completed, DO NOT poll.
                    if (res?.status === 'Completed') {
                        return of(res);
                    }
                    // Otherwise poll taskProgress until Completed
                    const taskId = res?.id ?? res;
                    if (!taskId) {
                        return throwError(() => new Error('Task ID is undefined'));
                    }
                    return timer(0, 3000).pipe(
                        exhaustMap(() => this.houseEventService.taskProgress(taskId).pipe(
                            retry({ count: 6, delay: 3000 })
                        )),
                        takeWhile((r: any) => r?.status !== 'Completed', true)
                    );
                }),
                // Safety: if anything completes unexpectedly, spinner will still turn off
                finalize(() => {
                    this.showGlobalSpinner = false;
                }),
                catchError((error) => throwError(() => error))
            )
            .subscribe({
                next: (completedTaskResponse: any) => {
                    if (completedTaskResponse?.status !== 'Completed') return;
                    this.showGlobalSpinner = false;
                    this.invalidEvents = completedTaskResponse?.rowErrors || [];
                    if (!this.invalidEvents.length) {
                        // success -> close dialog
                        this.snackBar.open('All notifications were successfully scheduled/sent.', 'OK', {
                            duration: 5000,
                            horizontalPosition: this.horizontalPosition,
                            verticalPosition: this.verticalPosition,
                            panelClass: ['adhoc-upload-snackbar', 'success-snackbar'],
                        });
                        this.uploadedSubject.next({ uploaded: true, partial: false });
                        this.resetFileSelection();
                        this.dialogRef.close({ uploaded: true });
                        return;
                    }
                    this.uploadedSubject.next({ uploaded: true, partial: true });
                    this.snackBar.open(
                        'Upload completed with errors. Please review the failed rows.',
                        'OK',
                        {
                            duration: 8000,
                            horizontalPosition: this.horizontalPosition,
                            verticalPosition: this.verticalPosition,
                            panelClass: ['adhoc-upload-snackbar', 'success-snackbar'],
                        }
                    );
                    this.disableUploadCta = true;
                    this.clearSelectedFileOnly();
                },
                error: () => {
                    this.showGlobalSpinner = false;
                    this.uploadedSubject.next({ uploaded: false, partial: false });
                    this.snackBar.open('Something went wrong, please try again later.', 'OK', {
                        duration: 8000,
                        horizontalPosition: this.horizontalPosition,
                        verticalPosition: this.verticalPosition,
                        panelClass: ['adhoc-upload-snackbar', 'error-snackbar'],
                    });
                },
            });
    }
    private isCsvEmpty(base64DataUrl: string): boolean {
        if (!base64DataUrl) return true;
        const base64 = base64DataUrl.includes(',')
            ? base64DataUrl.substring(base64DataUrl.indexOf(',') + 1)
            : base64DataUrl;
        let content = '';
        try {
            content = atob(base64);
        } catch {
            // If we can't decode, treat as empty so we fail safe with an error.
            return true;
        }
        const meaningfulLines = content
            .split(/\r\n|\n|\r/)
            .map((line) => line.trim())
            .filter((line) => line.length > 0 && line.replace(/,/g, '').trim().length > 0);
        // First meaningful line is the header; data rows must exist beyond it.
        return meaningfulLines.length <= 1;
    }
    resetFileSelection(): void {
        this.fileName = '';
        this.fileBase64 = null;
        this.fileError = false;
        this.fileNameMismatch = false;
        this.invalidFileMessage = '';
        this.disableUploadCta = true;
        this.invalidEvents = [];
        if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.value = '';
        }
    }
    private clearSelectedFileOnly(): void {
        // used when we want to keep errors visible but force new selection
        this.fileName = '';
        this.fileBase64 = null;
        this.fileError = false;
        this.fileNameMismatch = false;
        this.invalidFileMessage = '';
        this.disableUploadCta = true;
        if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.value = '';
        }
    }
}
